import { Member, MemberStatus } from '~/gen/proto/bff/v1/model_pb'

import { EffectivePlan } from './effectivePlans'

// Filter conditions applied to the member list. All conditions are combined
// with AND.
export type MemberFilterCondition = {
  // Plan codes to filter by. Empty means no plan filter. When role names are
  // also selected, the same plan must satisfy both conditions.
  planCodes: string[]
  // User role names to filter by. The Japanese role name is the canonical
  // key regardless of the UI language, so same-named roles across plans are
  // treated as one and the condition keeps its meaning when the language
  // changes. A member matches if it has a role with one of these names on a
  // selected plan (or any effective plan when no plan is selected). Empty
  // means no role filter.
  roleNames: string[]
  // Member status to filter by. undefined means no status filter (すべて).
  status: MemberStatus | undefined
  // Free-word keyword matched against name / english name / email.
  // Partial match, case-insensitive, no width normalization, no trimming.
  keyword: string
}

export const emptyMemberFilterCondition: MemberFilterCondition = {
  planCodes: [],
  roleNames: [],
  status: undefined,
  keyword: '',
}

// Whether any filter condition is currently applied.
export function isMemberFilterActive(
  condition: MemberFilterCondition,
): boolean {
  return (
    condition.planCodes.length > 0 ||
    condition.roleNames.length > 0 ||
    condition.status !== undefined ||
    condition.keyword !== ''
  )
}

// The member's assigned role codes on the plan, restricted to the plan's
// active roles. Member items carry role settings regardless of role status,
// while the filter options and CSV columns only cover active ones.
export function assignedRoleCodes(
  member: Member,
  plan: EffectivePlan,
): string[] {
  const settings = member.userRoleMap?.settingMap[plan.planCode]?.settings ?? []
  const activeCodes = new Set(plan.roles.map((role) => role.code))
  return settings
    .filter(
      (setting) => setting.isSetting && activeCodes.has(setting.userRoleCode),
    )
    .map((setting) => setting.userRoleCode)
}

// Plan and role conditions must be satisfied on the SAME plan: role names
// are not unique across plans, so checking them independently would match a
// member whose same-named role belongs to an unselected plan.
function matchesPlanAndRole(
  member: Member,
  planCodes: string[],
  roleNames: string[],
  plans: EffectivePlan[],
): boolean {
  if (planCodes.length === 0 && roleNames.length === 0) {
    return true
  }
  const targets = plans.filter(
    (plan) => planCodes.length === 0 || planCodes.includes(plan.planCode),
  )
  return targets.some((plan) => {
    const assigned = new Set(assignedRoleCodes(member, plan))
    if (roleNames.length === 0) {
      return assigned.size > 0
    }
    return plan.roles.some(
      (role) => assigned.has(role.code) && roleNames.includes(role.name),
    )
  })
}

function matchesStatus(
  member: Member,
  status: MemberStatus | undefined,
): boolean {
  if (status === undefined) {
    return true
  }
  return member.status === status
}

function matchesKeyword(member: Member, keyword: string): boolean {
  if (keyword === '') {
    return true
  }
  // Case-insensitive partial match. Width is NOT normalized (半角/全角 are
  // distinguished) and the keyword is NOT trimmed, per spec.
  const lowered = keyword.toLowerCase()
  const targets = [member.name, member.englishName, member.email]
  return targets.some((target) => target.toLowerCase().includes(lowered))
}

// Apply the AND-combined filter conditions to the member list.
export function filterMembers(
  members: Member[],
  condition: MemberFilterCondition,
  plans: EffectivePlan[],
): Member[] {
  return members.filter(
    (member) =>
      matchesPlanAndRole(
        member,
        condition.planCodes,
        condition.roleNames,
        plans,
      ) &&
      matchesStatus(member, condition.status) &&
      matchesKeyword(member, condition.keyword),
  )
}
